import { createFileRoute, Link, useParams } from '@tanstack/react-router'
import { SectionAssistant } from './-components/assistant/assistant-frame'
import { CRUMB_LINK_CLASS } from './-components/title-section'
import { Suspense, useState } from 'react'
import { noop, useSuspenseQuery } from '@tanstack/react-query'
import { useLingui } from '@lingui/react/macro'
import { PlusIcon, FileTextIcon } from 'lucide-react'
import { trpc } from '@/main'
import { clearProfileSelection } from '@/hooks/use-root-search'
import { useModalLink } from '@/components/custom/modal-slot'
import { ButtonLink } from '@/components/ui/button'
import { SearchInput } from '@/components/ui/search-input'
import { SectionLabel } from '@/components/custom/section-label'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { MembersPanelSkeleton, ReportPanelSkeleton } from './-components/app-layout-skeleton'
import { ListBodyPending } from './-components/shared/list-body-pending'
import { ListPanePending } from './-components/shared/list-pane-pending'
import {
  PageLayout,
  StickyPanel,
  ScrollableContent,
  SlideInPanel,
  CollapsibleListPanel,
} from './-components/shared/layout'
import { PanelEmptyState, PanelSelectionPrompt } from './-components/shared/panel-empty-state'
import {
  CollapsiblePanelProvider,
  CollapsiblePanel,
  CollapsiblePanelContent,
} from './-components/shared/collapsible-panel'
import { NAVIGATION_ICONS } from './-components/shared/navigation-config'
import { SectionIconTile } from './-components/shared/section-icon'
import {
  usePreviewMode,
  useIsLivePane,
  NavStackStage,
  NavStackScreen,
  useStackedLayout,
  NS_PASSTHROUGH_CLASS,
} from '@/components/custom/route-transition'
import { SectionStack } from './-components/shared/section-stack'
import { ReportScreenTitle } from './-components/report/report-screen-title'
import { ScrollableList } from './-components/shared/scrollable-list'
import { SelectionHint, CollapsibleSectionLabel, CollapsibleToolbar } from './-components/shared/collapsible'
import { TeamTreeItem } from './-components/teams/team-tree-item'
import { TeamActionMenu } from './-components/teams/team-action-menu'
import { teamDetail } from './-components/teams/team-detail'

const TeamsMembersPanel = teamDetail.pane((panes) => panes.TeamsMembersPanel)
const TeamReportContent = teamDetail.pane((panes) => panes.TeamReportContent)
const ReportPager = teamDetail.pane((panes) => panes.ReportPager)

export const Route = createFileRoute('/_app/teams')({
  // Every team the viewer owns or belongs to: the rail list, the breadcrumb, the header actions and the
  // team pickers all read THIS field off the context, so the key is the loader's by construction.
  context: () => ({ teamsListQueryOptions: trpc.teams.getTeams.queryOptions({}) }),
  loader: ({ context }) => {
    context.queryClient.query(context.teamsListQueryOptions).catch(noop)
  },
  component: RouteComponent,
  pendingComponent: ListPanePending,
  // Painted on the click frame; safe because nothing in the URL keys this match (no `loaderDeps`), so it never
  // re-keys under the reader.
})

function RouteComponent() {
  const isLivePane = useIsLivePane()
  const { t } = useLingui()
  // Inside a report-tab live preview (the pager's shadow router pinned to e.g. /teams/42/board), strip the
  // whole list/members/report split down to just the pager pane, so the preview shows the destination tab and
  // not a second copy of the Teams section. Only for a surface BELOW this one: the top-level section pager
  // previews from '/', which is ABOVE us and wants the full Teams section (see usePreviewMode).
  if (usePreviewMode('/teams') === 'passthrough') {
    return (
      <Suspense fallback={null}>
        <ReportPager className="h-full" />
      </Suspense>
    )
  }

  return (
    <div id={isLivePane ? 'report' : undefined} className="@container flex h-full flex-col">
      {/* On a phone the three panes below are STACKED SCREENS, not a squashed row: the teams list, the team's
          members, and the report, each pushed in from the right over the last (see nav-stack.tsx).
          The provider sits above PageLayout so the title bar can carry the stack's back chevron. */}
      <TeamsStack>
        <PageLayout
          icon={NAVIGATION_ICONS.teams}
          title={t`Teams`}
          breadcrumbsSlot={<TeamsBreadcrumbs />}
          titleActions={<TeamsTitleActions />}
        >
          <TeamsLayout />
        </PageLayout>
      </TeamsStack>
    </div>
  )
}

/**
 * Teams' mobile hierarchy — list → the team's members → its report. The standard section shape, so the whole
 * declaration is the three routes involved; `SectionStack` owns the depth derivation and the pop policy.
 */
function TeamsStack(props: { children: React.ReactNode }) {
  const { teamId } = useParams({ strict: false })
  return (
    <SectionStack list={{ to: '/teams' }} detail={teamId ? { to: '/teams/$teamId', params: { teamId } } : null}>
      {props.children}
    </SectionStack>
  )
}

function TeamsLayout() {
  const { t } = useLingui()
  const { teamId } = useParams({ strict: false })

  const hasTeamSelected = !!teamId

  // No viewport-driven auto-collapse: the nav stack gives each pane the WHOLE screen and decides which one is on
  // it, so both providers stay expanded and the collapse is desktop's business only.
  return (
    <CollapsiblePanelProvider defaultCollapsed={false}>
      {/* The panel row on desktop, the stack's stage on mobile — same div, same classes, see
          NavStackStage. Each pane is wrapped in the level it occupies in the mobile hierarchy; on
          desktop those wrappers are `display: contents` and this lays out exactly as it always did. */}
      <NavStackStage className="flex h-full min-h-0 w-full md:relative" style={{ containerType: 'inline-size' }}>
        {/* LEVEL 0 — Teams list (always visible on desktop, squashes to a 52px icon column when a team is
            selected; content keeps its full width inside and is clipped so only the avatars show) */}
        <NavStackScreen index={0} title={t`Teams`} leading={<SectionIconTile icon={NAVIGATION_ICONS.teams} />}>
          <CollapsibleListPanel isOpen={hasTeamSelected} className="print:hidden">
            <CollapsiblePanel expandedWidth="100%" disableTransition>
              {/* Single-list panel: the list view inside `CollapsiblePanelContent` is a `@container`
                    child of `SquashPanel`, so the same rendered list responsively collapses into a
                    centered avatar column (icon rail) at narrow widths via container queries. */}
              <CollapsiblePanelContent>
                <Suspense fallback={<ListBodyPending />}>
                  <TeamsPanelContent variant="sidebar" />
                </Suspense>
              </CollapsiblePanelContent>
            </CollapsiblePanel>
          </CollapsibleListPanel>
        </NavStackScreen>

        {/* Right Panel Group (Middle + Detail) - width animates from 0 to target. `NS_PASSTHROUGH_CLASS`: on
            mobile it stops being a box so the two levels inside position against the stage, not against
            this group's (0px / clipped) width. */}
        <SlideInPanel isOpen={hasTeamSelected} innerClassName="flex" className={NS_PASSTHROUGH_CLASS}>
          {/* LEVEL 1 — the team's members. The first screen a team opens onto on mobile. */}
          <NavStackScreen
            index={1}
            title={<TeamScreenTitle />}
            actions={teamId ? <TeamScreenActions teamId={teamId} /> : null}
          >
            <CollapsiblePanelProvider cookieName="panel_state_middle" keyboardShortcut="]" defaultCollapsed={false}>
              <StickyPanel className="print:hidden">
                <CollapsiblePanel>
                  <Suspense fallback={<MembersPanelSkeleton />}>
                    {teamId ? (
                      <TeamsMembersPanel teamId={teamId} />
                    ) : (
                      <PanelSelectionPrompt text={t`Select a team to view members`} />
                    )}
                  </Suspense>
                </CollapsiblePanel>
              </StickyPanel>
            </CollapsiblePanelProvider>
          </NavStackScreen>

          {/* LEVEL 2 — the report. Only mounts once a team is selected — `TeamReportContent` reads the
              team via `useTeam`, so it must never render without a teamId. With no team this slide-in
              panel is collapsed to width 0 anyway, so rendering nothing here is invisible. */}
          <NavStackScreen index={2} title={<ReportScreenTitle />}>
            <ScrollableContent>
              {hasTeamSelected ? (
                <Suspense fallback={<ReportPanelSkeleton />}>
                  <TeamReportContent />
                </Suspense>
              ) : null}
            </ScrollableContent>
          </NavStackScreen>
        </SlideInPanel>
        {/* Nothing selected: the empty detail side holds the assistant's conversation. */}
        <SectionAssistant shown={!hasTeamSelected} />
      </NavStackStage>
    </CollapsiblePanelProvider>
  )
}

/**
 * The title of the TEAM screen (nav stack level 1) — the team's own name, the same thing the desktop
 * breadcrumb's second crumb shows. Suspends on `getTeams`, which the section's loader has already prefetched,
 * so it fills in with the rest of the screen; the fallback keeps the bar's height either way.
 */
function TeamScreenTitle() {
  const { teamId } = useParams({ strict: false })
  if (!teamId) return null
  return (
    <Suspense fallback={null}>
      <TeamName teamId={teamId} />
    </Suspense>
  )
}

function TeamName(props: { teamId: string }) {
  const { data } = useSuspenseQuery(Route.useRouteContext().teamsListQueryOptions)
  const team = data.list.find((tm) => tm.id === Number(props.teamId))
  return <>{team?.name ?? ''}</>
}

/**
 * The TEAM screen's header actions: the team's own report (one push deeper, the same level a tapped member
 * lands on but with nobody selected — on desktop that pane is always beside the list, so there is nothing to
 * reach for there) and the owner's action menu.
 */
function TeamScreenActions(props: { teamId: string }) {
  const { t } = useLingui()
  return (
    <>
      <ButtonLink
        variant="ghost"
        size="icon"
        aria-label={t`Team report`}
        className="size-8"
        to="/teams/$teamId/report"
        params={{ teamId: props.teamId }}
        search={(prev) => clearProfileSelection(prev)}
      >
        <FileTextIcon className="size-4" />
      </ButtonLink>
      <Suspense fallback={null}>
        <TeamsTitleActionsLoaded />
      </Suspense>
    </>
  )
}

function TeamsBreadcrumbs() {
  const { t } = useLingui()
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink
            className={CRUMB_LINK_CLASS}
            render={
              <Link
                to="/teams"
                search={(prev) => ({
                  ...clearProfileSelection(prev),
                })}
              >
                {t`Teams`}
              </Link>
            }
          />
        </BreadcrumbItem>
        <Suspense fallback={null}>
          <TeamDetailCrumb />
        </Suspense>
      </BreadcrumbList>
    </Breadcrumb>
  )
}

function TeamDetailCrumb() {
  const { teamId } = useParams({ strict: false })
  const { data } = useSuspenseQuery(Route.useRouteContext().teamsListQueryOptions)
  if (!teamId) return null
  const team = data.list.find((tm) => tm.id === Number(teamId))
  if (!team?.name) return null
  return (
    <>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbLink
          className={CRUMB_LINK_CLASS}
          render={
            <Link
              to="/teams/$teamId/report"
              params={{ teamId }}
              search={(prev) => ({
                ...clearProfileSelection(prev),
              })}
            >
              {team.name}
            </Link>
          }
        />
      </BreadcrumbItem>
    </>
  )
}

function TeamsTitleActions() {
  return (
    <Suspense fallback={null}>
      <TeamsTitleActionsLoaded />
    </Suspense>
  )
}

function TeamsTitleActionsLoaded() {
  const { teamId } = useParams({ strict: false })
  const { data } = useSuspenseQuery(Route.useRouteContext().teamsListQueryOptions)
  // Owner list or member list — the SERVER already answered that (`viewerRole`), and it is the only
  // right answer here: an admin/authorized employee in company view reads the company's owned teams, so
  // this header menu belongs to them too. `TeamActionMenu` still gates writes on `teams:write`.
  if (!teamId || data.viewerRole === 'member') return null
  const selectedTeam = data.list.find((tm) => tm.id === Number(teamId))
  if (!selectedTeam) return null
  return <TeamActionMenu team={selectedTeam} variant="header" />
}

type PanelTeam = {
  id: number
  name: string
  type: 'myTeams' | 'otherTeams'
  membersCount?: number
  function?: number | null
}

// Reads the loader-prefetched `getTeams` (see this route's `loader`) and feeds the rail list view.
function TeamsPanelContent(props: { variant?: 'sidebar' | 'main' }) {
  const { data } = useSuspenseQuery(Route.useRouteContext().teamsListQueryOptions)
  return (
    <TeamsPanelView
      variant={props.variant}
      teams={data.list}
      isEmployee={data.viewerRole === 'member'}
      canCreateTeams={data.viewerCanCreate}
    />
  )
}

function TeamsPanelView(props: {
  variant?: 'sidebar' | 'main'
  teams: PanelTeam[]
  isEmployee: boolean
  canCreateTeams: boolean
}) {
  const { t } = useLingui()
  const isEmployee = props.isEmployee
  const canCreateTeams = props.canCreateTeams
  const teams = props.teams
  const { teamId } = useParams({ strict: false })
  const [searchQuery, setSearchQuery] = useState('')
  const createTeamLink = useModalLink('/@modal/teams/new')
  const findTeamLink = useModalLink('/@modal/teams/find')
  // In a stack this list is a whole level, and a pop always lands on it with the selection cleared — so its
  // hint must be on screen ALREADY while the level above is dragged off it, not appear on the commit and
  // shove the list down a row at the end of the gesture. (The rows' own selected highlight is suppressed
  // inside TeamTreeItem — see useStackedSelection.)
  const stacked = useStackedLayout()

  // Clear search when a team is selected so the collapsed rail shows all teams. Done DURING render via
  // React's documented "adjusting state when a prop changes" shape rather than in an effect, so the rail
  // never paints a frame of the filtered list after the selection has already landed.
  const [prevTeamId, setPrevTeamId] = useState(teamId)
  if (prevTeamId !== teamId) {
    setPrevTeamId(teamId)
    if (teamId) setSearchQuery('')
  }

  const filtered = teams.filter((team) => team.name.toLowerCase().includes(searchQuery.toLowerCase()))

  const myTeams = filtered.filter((team) => team.type === 'myTeams')
  const otherTeams = filtered.filter((team) => team.type === 'otherTeams')
  const allTeams = [...myTeams, ...otherTeams]
  const allTeamsCount = allTeams.length
  const myTeamsCount = myTeams.length
  const otherTeamsCount = otherTeams.length

  const showOnlyEmptyState = teams.length === 0 && !searchQuery

  const isMainVariant = props.variant === 'main'

  if (isMainVariant) {
    return (
      <div className="flex h-full flex-col">
        {!showOnlyEmptyState && (
          <>
            {/* Search bar with create button. Employees are view-only — they can't create teams. */}
            <div className="flex items-center gap-2 border-b p-3">
              <SearchInput placeholder={t`Search Teams`} value={searchQuery} onChange={setSearchQuery} />
              {canCreateTeams && (
                <ButtonLink variant="secondary" {...createTeamLink}>
                  <PlusIcon className="size-4" />
                  {t`Add`}
                </ButtonLink>
              )}
            </div>
          </>
        )}

        <ScrollableList
          isEmpty={allTeams.length === 0}
          emptyState={
            <PanelEmptyState
              noResults={allTeams.length === 0 && !!searchQuery}
              icon={<NAVIGATION_ICONS.teams className="text-muted-foreground size-5" />}
              title={isEmployee ? t`You haven't been added to any team` : t`No teams yet`}
              description={isEmployee ? undefined : t`Add teams to get started`}
              action={
                canCreateTeams ? (
                  <ButtonLink variant="secondary" {...createTeamLink}>
                    <PlusIcon className="size-4" />
                    {t`Add team`}
                  </ButtonLink>
                ) : undefined
              }
            />
          }
          hint={
            allTeams.length > 0 && (
              <SelectionHint visible={stacked || !teamId} text={t`Select a team to view its members and reports`} />
            )
          }
        >
          <SectionLabel className="px-2">
            {allTeamsCount === 1 ? t`${allTeamsCount} team` : t`${allTeamsCount} teams`}
          </SectionLabel>
          {isEmployee ? (
            // Employees don't own teams in this version, so the "my teams" vs "teams that added me"
            // split is meaningless to them — show every assigned team as a single flat list.
            allTeams.map((team) => (
              <TeamTreeItem
                key={team.id}
                team={team}
                isSelected={teamId === team.id.toString()}
                canManage={!isEmployee}
              />
            ))
          ) : (
            <>
              {myTeams.length > 0 && (
                <>
                  {otherTeams.length > 0 && (
                    <div className="p-2">
                      <SectionLabel>
                        {myTeams.length === 1 ? t`${myTeamsCount} team` : t`${myTeamsCount} teams`}
                      </SectionLabel>
                    </div>
                  )}
                  {myTeams.map((team) => (
                    <TeamTreeItem
                      key={team.id}
                      team={team}
                      isSelected={teamId === team.id.toString()}
                      canManage={!isEmployee}
                    />
                  ))}
                </>
              )}

              {otherTeams.length > 0 && (
                <>
                  {myTeams.length > 0 && (
                    <div className="p-2">
                      <SectionLabel>
                        {otherTeams.length === 1
                          ? t`${otherTeamsCount} team that added me`
                          : t`${otherTeamsCount} teams that added me`}
                      </SectionLabel>
                    </div>
                  )}
                  {otherTeams.map((team) => (
                    <TeamTreeItem
                      key={team.id}
                      team={team}
                      isSelected={teamId === team.id.toString()}
                      canManage={!isEmployee}
                    />
                  ))}
                </>
              )}
            </>
          )}
        </ScrollableList>
      </div>
    )
  }

  return (
    <>
      {!showOnlyEmptyState && (
        <>
          <CollapsibleToolbar>
            <CollapsibleToolbar.Search searchLink={findTeamLink} searchLabel={t`Search Teams`}>
              <SearchInput placeholder={t`Search Teams`} value={searchQuery} onChange={setSearchQuery} />
            </CollapsibleToolbar.Search>
            {/* Employees are view-only — no create action. */}
            {canCreateTeams && (
              <CollapsibleToolbar.Action
                icon={<PlusIcon className="size-4" />}
                tooltip={t`Add team`}
                link={createTeamLink}
              />
            )}
          </CollapsibleToolbar>
        </>
      )}

      <ScrollableList
        isEmpty={allTeams.length === 0}
        emptyState={
          <PanelEmptyState
            noResults={allTeams.length === 0 && !!searchQuery}
            icon={<NAVIGATION_ICONS.teams className="text-muted-foreground size-5" />}
            title={isEmployee ? t`You haven't been added to any team` : t`No teams yet`}
            description={isEmployee ? undefined : t`Add teams to get started`}
            action={
              canCreateTeams ? (
                <ButtonLink variant="secondary" {...createTeamLink}>
                  <PlusIcon className="size-4" />
                  {t`Add team`}
                </ButtonLink>
              ) : undefined
            }
          />
        }
        hint={
          allTeams.length > 0 && (
            <SelectionHint visible={stacked || !teamId} text={t`Select a team to view its members and reports`} />
          )
        }
      >
        {isEmployee ? (
          // Employees don't own teams — show every assigned team in one flat list (no my/other split).
          <>
            <CollapsibleSectionLabel>
              {allTeamsCount === 1 ? t`${allTeamsCount} team` : t`${allTeamsCount} teams`}
            </CollapsibleSectionLabel>
            {allTeams.map((team) => (
              <TeamTreeItem
                key={team.id}
                team={team}
                isSelected={teamId === team.id.toString()}
                canManage={!isEmployee}
              />
            ))}
          </>
        ) : (
          <>
            {myTeams.length > 0 && (
              <>
                <CollapsibleSectionLabel>
                  {myTeams.length === 1 ? t`${myTeamsCount} team` : t`${myTeamsCount} teams`}
                </CollapsibleSectionLabel>
                {myTeams.map((team) => (
                  <TeamTreeItem
                    key={team.id}
                    team={team}
                    isSelected={teamId === team.id.toString()}
                    canManage={!isEmployee}
                  />
                ))}
              </>
            )}

            {otherTeams.length > 0 && (
              <>
                <CollapsibleSectionLabel>
                  {otherTeams.length === 1
                    ? t`${otherTeamsCount} team that added me`
                    : t`${otherTeamsCount} teams that added me`}
                </CollapsibleSectionLabel>
                {otherTeams.map((team) => (
                  <TeamTreeItem
                    key={team.id}
                    team={team}
                    isSelected={teamId === team.id.toString()}
                    canManage={!isEmployee}
                  />
                ))}
              </>
            )}
          </>
        )}
      </ScrollableList>
    </>
  )
}
